import { useEffect, useState, useRef, type FormEvent } from 'react';
import { Container, HStack, styled } from '../styled-system/jsx';
import { Button } from '../src/components/Button';
import { Input } from '../src/components/Input';
import { Label } from '../src/components/Label';
import { MULTIROUTERS_ENDPOINT, MULTIROUTERS_ENDPOINT_DEV } from './config';

const Title = styled('h1', {
    base: {
        fontSize: '2xl',
        fontWeight: 'bold',
        color: 'main.text.2',
        marginBottom: '4',
    }
});

const Form = styled('form', {
    base: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4',
        width: '100%',
        maxWidth: '480px',
    }
});

const Field = styled('div', {
    base: {
        display: 'flex',
        flexDirection: 'column',
        gap: '2',
        width: '100%',
    }
});

const NativeSelect = styled('select', {
    base: {
        fontSize: 'sm',
        color: 'main.text.2',
        outline: 'none',
        width: '100%',
        padding: '4',
        rounded: 'lg',
        border: '1px solid',
        bg: 'main.background.1',
        borderColor: 'main.border.1',
        cursor: 'pointer',

        _focus: {
            borderColor: 'main.solid.1',
        },

        _disabled: {
            opacity: 0.5,
            cursor: 'not-allowed',
        },
    }
});

const CheckboxLabel = styled('label', {
    base: {
        display: 'flex',
        alignItems: 'center',
        gap: '2',
        fontSize: 'sm',
        color: 'main.text.2',
        cursor: 'pointer',

        '&:has(input:disabled)': {
            opacity: 0.5,
            cursor: 'not-allowed',
        },
    }
});

const Checkbox = styled('input', {
    base: {
        width: '16px',
        height: '16px',
        accentColor: 'main.solid.1',
        cursor: 'pointer',

        _disabled: {
            cursor: 'not-allowed',
        },
    }
});

export default function ItineraryForm() {

    const urlParams = new URLSearchParams(window.location.search);
    const getGraphParam = () => urlParams.get('network') || urlParams.get('graph') || null;

    const [graphsList, setGraphList] = useState<string[]|null>(null);
    const [selectedGraph, setSelectedGraph] = useState<string>(getGraphParam() || '-');
    const selectedGraphRef = useRef<string>(getGraphParam() || '-');
    const defaultEmmid = urlParams.get('emmid') ? Number(urlParams.get('emmid')) : null;
    const defaultGraphManagement = urlParams.get('graphmanagement') === 'true';
    const [useDevRouters, setUseDevRouters] = useState(urlParams.get('devEnvironment') === 'true');

    useEffect(() => {
        const endpoint = useDevRouters ? MULTIROUTERS_ENDPOINT_DEV : MULTIROUTERS_ENDPOINT;
        const currentGraph = selectedGraphRef.current;
        fetch(`${endpoint}/list`)
            .then(res => res.json())
            .then((graphs: string[]) => {
                const sortedGraphs = [...graphs].sort();
                setGraphList(sortedGraphs);
                if (sortedGraphs.includes(currentGraph) && currentGraph !== '-') {
                    setSelectedGraph(currentGraph);
                    selectedGraphRef.current = currentGraph;
                } else {
                    setSelectedGraph('-');
                    selectedGraphRef.current = '-';
                }
            })
            .catch(error => {
                console.error('Error fetching graph list:', error);
                setGraphList([]);
                setSelectedGraph('-');
                selectedGraphRef.current = '-';
            });
    }, [useDevRouters]);

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        const form = event.currentTarget;

        event.preventDefault();
        event.stopPropagation();

        if (!form.reportValidity()) {
            return;
        }

        const formData = new FormData(form);
        const formObj = Object.fromEntries(formData.entries());
        const emmid = formData.get('emmid');
        const { graphManagement } = formObj;
        const graph = formData.get('graph') !== '-' ? formData.get('graph') : '';

        const nextParams = new URLSearchParams(window.location.search);

        nextParams.set('emmid', emmid as string);
        nextParams.set('graph', graph as string);
        nextParams.set('metarouterurl', MULTIROUTERS_ENDPOINT);
        nextParams.set('routingmode', 'walking');
        nextParams.set('routingtype', 'osrm');
        nextParams.set('routingurl', 'https://routing-osrm.getwemap.com');

        if (graphManagement) {
            nextParams.set('graphmanagement', 'true');
        }

        if (useDevRouters) {
            nextParams.set('devEnvironment', 'true');
            nextParams.set('metarouterurl', MULTIROUTERS_ENDPOINT_DEV);
        }

        location.search = nextParams.toString();
    };

    return (
        <Container py="8">
            <Title>Parameters</Title>

            <Form noValidate onSubmit={handleSubmit}>
                <Input
                    id="emmid"
                    type="number"
                    name="emmid"
                    required
                    label="Emmid"
                    placeholder="Enter emmid"
                    defaultValue={defaultEmmid ?? undefined}
                />

                <Field>
                    <HStack justify="space-between" alignItems="center">
                        <Label htmlFor="graph">Graph</Label>
                        <CheckboxLabel>
                            <Checkbox
                                type="checkbox"
                                name="environment"
                                checked={useDevRouters}
                                onChange={(e) => setUseDevRouters(e.target.checked)}
                            />
                            Dev environment
                        </CheckboxLabel>
                    </HStack>
                    {graphsList && (
                        <NativeSelect
                            id="graph"
                            name="graph"
                            aria-label="Graph selection"
                            value={selectedGraph}
                            onChange={(e) => {
                                const value = e.target.value;
                                setSelectedGraph(value);
                                selectedGraphRef.current = value;
                            }}
                        >
                            <option>-</option>
                            {[...graphsList].sort().map((graphName) => (
                                <option key={graphName} value={graphName}>
                                    {graphName}
                                </option>
                            ))}
                        </NativeSelect>
                    )}
                </Field>

                <CheckboxLabel>
                    <Checkbox
                        type="checkbox"
                        name="graphManagement"
                        defaultChecked={defaultGraphManagement}
                        disabled
                    />
                    Use graph management (enable/disable ways)
                </CheckboxLabel>

                <Button type="submit">
                    Submit
                </Button>
            </Form>
        </Container>
    );
}
